import React, { FC, useMemo } from 'react'
import { Modal, Button, Popconfirm } from 'antd'
import { useHideModal } from 'components/Modals'
import { useLocalization } from 'localization'
import { Formik } from 'formik'
import { Form, FormItem } from 'formik-antd'
import { createValidationSchema } from 'utils/validation'
import FormikInput from 'components/FormikInput'
import { IWallet, useCreateWallet, useEditWallet, useWalletById, useWallets } from 'api'
import { IWalletFormValues } from './types'
import WalletProperties from './WalletProperties'

const schema = createValidationSchema(yup =>
	yup.object({
		name: yup.string().min(3).required(),
		address: yup.string().optional(),
	})
)

export interface ICreateWalletModal {
	id?: IWallet['id']
}

const CreateWalletModal: FC<ICreateWalletModal> = ({ id }) => {
	const { t } = useLocalization()
	const hideModal = useHideModal()
	const { mutateAsync: createWallet } = useCreateWallet()
	const { mutateAsync: editWallet } = useEditWallet()

	const { data: wallets = [] } = useWallets()
	const { data: existingWallet } = useWalletById(id)

	const initialValue: IWalletFormValues = useMemo(
		() => ({
			name: existingWallet?.name || '',
			address: existingWallet?.address || '',
			properties: existingWallet ? existingWallet.properties : undefined,
		}),
		[existingWallet]
	)

	const onSubmit = async (values: IWalletFormValues) => {
		if (id)
			await editWallet({
				id,
				...values,
			})
		else await createWallet(values)
		hideModal()
	}

	return (
		<Modal
			title={id ? t('actions.editWallet') : t('actions.createWallet')}
			width={600}
			open
			onCancel={hideModal}
			footer={null}
		>
			<Formik<IWalletFormValues>
				initialValues={initialValue}
				enableReinitialize
				validationSchema={schema}
				onSubmit={onSubmit}
			>
				{({ dirty, isValid, isSubmitting, handleSubmit, values }) => {
					const shouldDisplayConfirmation = wallets.some(
						({ name, id: walletId }) => name === values.name && id !== walletId
					)

					return (
						<Form layout="vertical">
							<FormItem name="name" label={t('terms.walletName')} required>
								<FormikInput name="name" placeholder={t('placeholders.walletName')} />
							</FormItem>
							<FormItem name="address" label={t('terms.walletAddress')}>
								<FormikInput name="address" placeholder={t('placeholders.walletAddress')} />
							</FormItem>
							<WalletProperties isEdit={!!id} />
							{shouldDisplayConfirmation ? (
								<Popconfirm
									title={t('wallets.add.confirmation')}
									onConfirm={() => handleSubmit()}
									okText={t('actions.confirm')}
									cancelText={t('actions.cancel')}
									icon={null}
								>
									<Button type="primary" htmlType="button" loading={isSubmitting} disabled={!isValid || !dirty}>
										{t(id ? 'actions.edit' : 'actions.add')}
									</Button>
								</Popconfirm>
							) : (
								<Button type="primary" htmlType="submit" loading={isSubmitting} disabled={!isValid || !dirty}>
									{t(id ? 'actions.edit' : 'actions.add')}
								</Button>
							)}
						</Form>
					)
				}}
			</Formik>
		</Modal>
	)
}

export default CreateWalletModal
